import React, { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  completeTutorialStep,
  hydrateTutorial,
  loadTutorialState,
  saveTutorialState,
  selectTutorialStep,
  skipTutorial,
} from '../store/tutorialSlice';
import { TUTORIAL_SKIP, TUTORIAL_SPEAKER } from '../tutorialSteps';
import { useTranslation } from 'react-i18next';
import { tr } from '../../../i18n/msg';
import '../styles/tutorial.css';

// Пауза между появлением цели и показом подсказки: пережидаем анимации/переходы,
// чтобы рука не прыгала по экрану.
const SHOW_DELAY_MS = 400;
const HAND_W = 96;
const HAND_H = 137;
// Кончик когтя на картинке hand.png (440×628) → в пикселях отрисованной руки
const TIP_X = (192 / 440) * HAND_W;
const TIP_Y = (55 / 628) * HAND_H;
const PAD = 6; // отступ подсветки вокруг элемента
const MIN_SIDE_BUBBLE = 200;

const findTarget = (target) => document.querySelector(`[data-tutorial="${target}"]`);

const sameRect = (a, b) =>
  a && b &&
  Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5 &&
  Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5;

/**
 * Следит за элементом [data-tutorial=target] и возвращает его прямоугольник
 * (null, пока элемента нет или он нулевого размера).
 */
function useTargetRect(target, enabled) {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!enabled || !target) {
      setRect(null);
      return undefined;
    }
    let frame;
    let last; // undefined → первый тик всегда сбрасывает прямоугольник прошлой цели
    const tick = () => {
      const el = findTarget(target);
      const r = el?.getBoundingClientRect();
      // Цель перекрыта модалкой/попапом (элементы оверлея — pointer-events: none) — ждём
      const onTop = r && el.contains(document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2));
      const next = onTop && r.width > 0 && r.height > 0
        ?{ left: r.left, top: r.top, width: r.width, height: r.height, radius: parseFloat(getComputedStyle(el).borderRadius) || 12 }
        : null;
      if (!(next === last || sameRect(next, last))) {
        last = next;
        setRect(next);
      }
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [target, enabled]);

  return rect;
}

/**
 * Оверлей туториала: затемнение с «окном» над целью, кибер-рука и реплика робота.
 * Шаги и их порядок — в tutorialSteps.js. Монтируется один раз в AppLayout.
 */
export default function TutorialOverlay() {
  const dispatch = useDispatch();
  const location = useLocation();
  const userId = useSelector((s) => s.user.data?.id);
  const tutorial = useSelector((s) => s.tutorial);
  const step = useSelector(selectTutorialStep);
  const rewardPopupOpen = useSelector((s) => s.rewardsQueue.queue.length > 0);
  const { t } = useTranslation();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (userId) dispatch(hydrateTutorial({ userId, saved: loadTutorialState(userId) }));
  }, [userId, dispatch]);

  useEffect(() => {
    if (tutorial.userId && tutorial.userId === userId && tutorial.status !== 'idle') {
      saveTutorialState(userId, tutorial);
    }
  }, [tutorial, userId]);

  const enabled = Boolean(step) && !rewardPopupOpen && (!step.route || step.route === location.pathname);
  const rect = useTargetRect(step?.target, enabled);
  const hasRect = Boolean(rect);

  useEffect(() => {
    setReady(false);
    if (!hasRect) return undefined;
    const timer = setTimeout(() => setReady(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [hasRect, step?.id]);

  // Тап по подсвеченному элементу закрывает шаг (сам клик проходит в игру как обычно)
  useEffect(() => {
    if (!step || step.completeOn !== 'click' || !ready) return undefined;
    const onClick = (e) => {
      const el = findTarget(step.target);
      if (el && el.contains(e.target)) dispatch(completeTutorialStep(step.id));
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [step, ready, dispatch]);

  if (!step || !rect || !ready) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const hole = {
    left: Math.max(0, rect.left - PAD),
    top: Math.max(0, rect.top - PAD),
    right: Math.min(vw, rect.left + rect.width + PAD),
    bottom: Math.min(vh, rect.top + rect.height + PAD),
  };

  const anchor = step.anchor || { x: rect.width > 160 ? 0.25 : 0.5, y: 0.5 };
  const tipX = rect.left + rect.width * anchor.x;
  const tipY = rect.top + rect.height * anchor.y;
  // Рука приходит сверху: корпусом влево-вверх, а у левого края экрана — вправо-вверх
  const handFromLeft = tipX > 110;
  const rotation = handFromLeft ? 160 : 200;

  const text = tr(t, step.text);
  const sideSpace = handFromLeft ? vw - tipX - 26 : tipX - 26;
  const bubbleStyle = sideSpace >= MIN_SIDE_BUBBLE
    ? {
        bottom: vh - hole.top + 18,
        ...(handFromLeft ? { right: 16, left: Math.max(16, tipX + 10) } : { left: 16, right: Math.max(16, vw - tipX + 10) }),
      }
    : { bottom: vh - hole.top + HAND_H + 10, left: 16, right: 16 };

  return createPortal(
    <div className="tut-root" role="dialog" aria-live="polite" aria-label={text || t('tutorial.aria', 'Tutorial')}>
      {/* Четыре затемнённые панели вокруг цели блокируют остальные тапы; сама цель кликабельна */}
      <div className="tut-shade" style={{ left: 0, top: 0, width: vw, height: hole.top }} />
      <div className="tut-shade" style={{ left: 0, top: hole.bottom, width: vw, height: vh - hole.bottom }} />
      <div className="tut-shade" style={{ left: 0, top: hole.top, width: hole.left, height: hole.bottom - hole.top }} />
      <div className="tut-shade" style={{ left: hole.right, top: hole.top, width: vw - hole.right, height: hole.bottom - hole.top }} />

      <div
        className="tut-spot"
        style={{
          left: hole.left,
          top: hole.top,
          width: hole.right - hole.left,
          height: hole.bottom - hole.top,
          borderRadius: rect.radius + PAD,
        }}
      />

      <div className="tut-ring" style={{ left: tipX, top: tipY }} />

      <div className="tut-hand" style={{ left: tipX, top: tipY, transform: `rotate(${rotation}deg)` }}>
        <img
          src="/tutorial/hand.png"
          alt=""
          draggable={false}
          style={{ width: HAND_W, height: HAND_H, left: -TIP_X, top: -TIP_Y, transformOrigin: `${TIP_X}px ${TIP_Y}px` }}
        />
      </div>

      {text && (
        <div className="tut-bubble" style={bubbleStyle}>
          <div className="tut-bubble-head">
            <span className="tut-bubble-speaker">{TUTORIAL_SPEAKER}</span>
            <button type="button" className="tut-skip" onClick={() => dispatch(skipTutorial())}>
              {tr(t, TUTORIAL_SKIP)}
            </button>
          </div>
          <div className="tut-bubble-text">{text}</div>
        </div>
      )}
    </div>,
    document.body,
  );
}
